<template>
  <main class="flyer-page print-friendly">
    <div class="flyer-container">
      <div class="header-section">
        <div class="logo-section">
          <img src="/assets/img/logo.png" alt="Space Coast STEM Logo" class="logo" />
        </div>
        <div class="tagline">
          <h1>Empowering Students Through Technology</h1>
          <p class="subtitle">Free Laptops for Brevard County Students</p>
        </div>
      </div>

      <!-- Main Info Section -->
      <div class="info-section">
        <div class="mission-box">
          <h2>Our Mission</h2>
          <p>
            Space Coast STEM provides <strong>free refurbished laptops</strong> to students in Brevard County who lack access to technology at home.
            We believe every student deserves the tools they need to succeed in today's digital world.
          </p>
        </div>

      

        <div class="qr-section">
          <div class="qr-box">
            <canvas ref="qrCodeCanvas" class="qr-canvas"></canvas>
            <div class="qr-text">
              <h3>Request a Laptop</h3>
              <p>Scan to fill out our simple request form</p>
              <p class="url">spacecoaststem.org/request</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Teacher Signup Section -->
      <div class="teacher-section">
        <div class="teacher-box">
          <h2>Teachers: Help Connect Students in Need</h2>
          <p>
            As an educator, you can help identify students would benefit from this program and help get them the tools they need. Use the teacher portal to approve requests and claim equipment for your students.
          </p>
          <div class="signup-info">
            <div class="signup-icon">👨‍🏫</div>
            <div class="signup-details">
              <h3>Teacher Portal Signup</h3>
              <p class="signup-url"><strong>portal.spacecoaststem.org/signup</strong></p>
              <p class="signup-description">Create an account to submit and track student laptop requests</p>
            </div>
          </div>
        </div>
      </div>


    </div>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import QRCode from 'qrcode'

const qrCodeCanvas = ref<HTMLCanvasElement | null>(null)

onMounted(async () => {
  if (qrCodeCanvas.value) {
    try {
      await QRCode.toCanvas(qrCodeCanvas.value, 'https://spacecoaststem.org/request', {
        width: 200,
        margin: 1,
        color: {
          dark: '#2c4a6b',
          light: '#ffffff'
        }
      })
    } catch (error) {
      console.error('Error generating QR code:', error)
    }
  }
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

.flyer-page {
  font-family: 'Inter', sans-serif;
  background: white;
  min-height: 100vh;
}

.flyer-container {
  max-width: 8.5in;
  margin: 0 auto;
  padding: 0.5in;
  background: white;
}

/* Header Section */
.header-section {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 4px solid #3ba3d1;
}

.logo-section {
  flex-shrink: 0;
}

.logo {
  width: 180px;
  height: auto;
}

.tagline {
  flex: 1;
}

.tagline h1 {
  font-size: 2rem;
  font-weight: 800;
  color: #2c4a6b;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
}

.subtitle {
  font-size: 1.2rem;
  color: #3ba3d1;
  font-weight: 700;
  margin: 0;
}

/* Mission Box */
.mission-box {
  background: linear-gradient(135deg, rgba(44, 74, 107, 0.05) 0%, rgba(59, 163, 209, 0.05) 100%);
  border-left: 5px solid #2c4a6b;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.mission-box h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
}

.mission-box p {
  font-size: 1.1rem;
  line-height: 1.7;
  color: #333;
  margin: 0;
}

.mission-box strong {
  color: #2c4a6b;
}

/* How It Works */
.how-it-works {
  margin-bottom: 1.5rem;
}

.how-it-works h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 1rem 0;
  text-align: center;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: white;
  border: 2px solid #3ba3d1;
  border-radius: 10px;
  padding: 1rem;
}

.step-number {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #2c4a6b 0%, #3ba3d1 100%);
  color: white;
  font-size: 1.8rem;
  font-weight: 800;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.step-content h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.5rem 0;
}

.step-content p {
  font-size: 0.95rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

/* QR Section */
.qr-section {
  margin-bottom: 1.5rem;
}

.qr-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  background: linear-gradient(135deg, rgba(44, 74, 107, 0.03) 0%, rgba(59, 163, 209, 0.03) 100%);
  border: 3px solid #3ba3d1;
  border-radius: 12px;
  padding: 1.5rem;
}

.qr-canvas {
  width: 200px;
  height: 200px;
  flex-shrink: 0;
}

.qr-text {
  text-align: left;
}

.qr-text h3 {
  font-size: 1.5rem;
  font-weight: 800;
  color: #2c4a6b;
  margin: 0 0 0.5rem 0;
}

.qr-text p {
  font-size: 1.1rem;
  color: #333;
  margin: 0.25rem 0;
  line-height: 1.5;
}

.qr-text .url {
  font-weight: 700;
  color: #3ba3d1;
  font-size: 1.2rem;
  margin-top: 0.5rem;
}

/* Teacher Section */
.teacher-section {
  margin-bottom: 1.5rem;
}

.teacher-box {
  background: #f5f7fa;
  border: 3px solid #2c4a6b;
  border-radius: 12px;
  padding: 1.5rem;
}

.teacher-box h2 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
  text-align: center;
}

.teacher-box > p {
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
  margin: 0 0 1rem 0;
  text-align: center;
}

.signup-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: white;
  border: 2px solid #3ba3d1;
  border-radius: 10px;
  padding: 1rem 1.5rem;
}

.signup-icon {
  font-size: 3.5rem;
  flex-shrink: 0;
}

.signup-details h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.5rem 0;
}

.signup-url {
  font-size: 1.3rem;
  color: #3ba3d1;
  margin: 0.5rem 0;
}

.signup-url strong {
  font-weight: 800;
}

.signup-description {
  font-size: 0.95rem;
  color: #666;
  margin: 0.5rem 0 0 0;
  font-style: italic;
}

/* Contact Section */
.contact-section {
  background: linear-gradient(135deg, #2c4a6b 0%, #3ba3d1 100%);
  border-radius: 10px;
  padding: 1rem;
  color: white;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.contact-item {
  text-align: center;
  font-size: 0.95rem;
}

.contact-item strong {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 700;
}

/* Print Styles */
@media print {
  .flyer-page {
    background: white;
  }

  .flyer-container {
    padding: 0.4in;
    max-width: none;
  }

  @page {
    margin: 0.25in;
    size: letter portrait;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  .header-section,
  .mission-box,
  .how-it-works,
  .qr-section,
  .teacher-section,
  .contact-section,
  .step,
  .qr-box,
  .teacher-box,
  .signup-info {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  h1, h2, h3 {
    page-break-after: avoid;
    break-after: avoid;
  }

  .steps-grid,
  .contact-grid {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}

/* Responsive for screen viewing */
@media (max-width: 768px) {
  .flyer-container {
    padding: 1rem;
  }

  .header-section {
    flex-direction: column;
    text-align: center;
  }

  .logo {
    width: 150px;
  }

  .tagline h1 {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .steps-grid {
    grid-template-columns: 1fr;
  }

  .qr-box {
    flex-direction: column;
    text-align: center;
  }

  .qr-text {
    text-align: center;
  }

  .qr-canvas {
    width: 180px;
    height: 180px;
  }

  .signup-info {
    flex-direction: column;
    text-align: center;
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }
}
</style>
